import { ButtonBase, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { percentage } from "../../shared/percentage";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { QuantumLiabilityEditorDialog } from "./QuantuLiabilityEditorDialog";

export interface QuantumProps {
  issueTitle: string;
  issueOrdinal: string;
  quantumTitle: string;
  quantumAmount: number;
}

/**
 * What one defendant is liable for of a claimed amount, as a button that opens
 * the editor of that liability.
 */
export const QuantumCell = ({
  quantum,
  defendant,
  liability,
  onLiabilityUpdate,
}: {
  quantum: QuantumProps;
  defendant: string;
  liability: number;
  onLiabilityUpdate: (newChance: number) => void;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.quantums.liability",
  });
  const { readOnly } = useCase();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const tooltip = t("tooltip", {
    defendant,
    quantumTitle: quantum.quantumTitle,
  });

  return (
    <>
      <QuantumLiabilityEditorDialog
        quantum={quantum}
        defendant={defendant}
        liability={liability}
        onClose={(newLiability) => {
          setEditDialogOpen(false);
          if (newLiability !== undefined) onLiabilityUpdate(newLiability);
        }}
        open={editDialogOpen}
      />

      <ButtonBase
        title={tooltip}
        aria-label={tooltip}
        disabled={readOnly}
        onClick={() => setEditDialogOpen(true)}
        sx={{
          // a bare button has no look of its own for being disabled
          "&.Mui-disabled": { opacity: theme.palette.action.disabledOpacity },
          width: "100%",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderRadius: 1.75,
          // no liability is a gap in the row, so its button is only outlined
          border: `1px ${liability === 0 ? "dashed" : "solid"} ${theme.palette.divider}`,
          bgcolor: "background.paper",
          ...TABULAR_NUMERALS,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {liability === 0 ? (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            {t("none")}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {percentage.format(liability)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {currency.format(quantum.quantumAmount * liability)}
            </Typography>
          </>
        )}
      </ButtonBase>
    </>
  );
};
